import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import DataGrid from '../../../components/DataGrid';
import Forms from '../../../components/Forms';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import {useModal} from '../../../contexts/ModalContext';
import {ReactComponent as DeleteIcon} from '../../../assets/images/delete.svg';
import {ReactComponent as EditIcon} from '../../../assets/images/edit.svg';
import LyricsCategoryModal from './LyricsCategoryModal';

const LyricsCategories = () => {
    const {t} = useTranslation();
    const {openModal, confirm} = useModal();
    const [categories, setCategories] = useState([]);
    const [filterText, setFilterText] = useState('');

    const fetchCategories = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.lyricsCategories.list);
            setCategories(res.data || []);
        } catch (e) {
            console.error('Failed to fetch lyrics categories:', e);
        }
    }, []);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    const openCategoryModal = (category) => {
        openModal(
            LyricsCategoryModal,
            { category, onCategoryUpdate: fetchCategories },
            { title: category ? t('lyricsCategories.edit') : t('lyricsCategories.add') }
        );
    };

    const confirmDelete = async (category) => {
        const ok = await confirm(
            t('lyricsCategories.confirmDeletion', { name: category.name }),
            {}
        );
        if (ok === true) {
            try {
                await API.delete(API_ENDPOINTS.lyricsCategories.delete(category._id));
                setCategories(prev => prev.filter(c => c._id !== category._id));
            } catch (e) {
                console.error('Failed to delete lyrics category:', e);
            }
        }
    };

    const rows = categories.filter(c =>
        (c.name || '').toLowerCase().includes(filterText.trim().toLowerCase())
    );

    const columns = [
        {
            key: 'name',
            name: t('lyricsCategories.name'),
            width: '45%',
            renderCell: ({row}) => (
                <label className="flex justify-between">
                    {row.name}
                    <div className="flex">
                        <DeleteIcon className="w-4 cursor-pointer fill-red-700 m-2" onClick={() => confirmDelete(row)} />
                        <EditIcon className="w-4 cursor-pointer text-blue-800 h-full" onClick={() => openCategoryModal(row)} />
                    </div>
                </label>
            )
        },
        {
            key: 'slug',
            name: t('lyricsCategories.slug'),
            width: '25%',
            renderCell: ({row}) => row.slug || '-'
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            width: '15%',
            renderCell: ({row}) => row.createdAt ? moment(row.createdAt).format('DD.MM.YYYY HH:mm') : '-'
        },
        {
            key: 'updatedAt',
            name: t('updatedAt'),
            width: '15%',
            renderCell: ({row}) => row.updatedAt ? moment(row.updatedAt).format('DD.MM.YYYY HH:mm') : '-'
        }
    ].map(c => ({...c, resizable: true}));

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <Forms.Button label={t('lyricsCategories.add')} onClick={() => openCategoryModal()} />
                <div className="grid grid-cols-1 gap-3 items-end">
                    <Forms.Text
                        label={t('search')}
                        placeholder={t('lyricsCategories.searchPlaceholder')}
                        value={filterText}
                        onChange={setFilterText}
                    />
                </div>
            </div>

            <DataGrid rows={rows} columns={columns} />
        </div>
    );
};

export default LyricsCategories;
